// Team Members list page (CHG-1249 / ITER-3373; removal parity CHG-3357).
//
// Generated fields and fetching remain in teamMemberListConfig. The confirmed
// remove-member command is hand-written here because the list generator does
// not yet model row mutation state or destructive confirmation dialogs.
//
// Spec: specs/tier-05-experience/team-members-v2.cross-screen.yaml.

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { UserMinus } from "lucide-react";
import { ListPage } from "../components/composition";
import { FormDialog } from "../components/composition/FormDialog";
import type { DataTableColumn } from "../components/composition/DataTable";
import type { ListPageConfig } from "../components/composition/ListPage";
import { useHasPermission } from "../auth/usePermissions";
import { GATE_CATALOG } from "../auth/gateCatalog";
import { useAuth } from "../contexts/AuthContext";
import { teamMembersApi, type TeamMember } from "../api/team-members";
import { teamMemberListConfig } from "../generated/screens/team-member-list";

const REMOVE_FAILURE = "Could not remove this team member. Please try again.";

function removeErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
  }
  return REMOVE_FAILURE;
}

export function TeamMembers() {
  const { account } = useAuth();
  const canRemove = useHasPermission(GATE_CATALOG.team_list["remove"].permission);
  const queryClient = useQueryClient();
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => teamMembersApi.removeTeamMember(memberId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["team-members", "list"] });
      setRemoveTarget(null);
    },
  });

  const config = useMemo<ListPageConfig<TeamMember>>(() => {
    if (!account || !canRemove) return teamMemberListConfig;

    const actionColumn: DataTableColumn<TeamMember> = {
      field: "__actions",
      label: "Actions",
      type: "custom",
      width: "1%",
      render: (member) => {
        const isSelf =
          member.id === account.id ||
          member.email.trim().toLowerCase() === account.email.trim().toLowerCase();
        if (isSelf) return null;
        return (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={`Remove ${member.full_name}`}
            title={`Remove ${member.full_name}`}
            onClick={(event) => {
              event.stopPropagation();
              setRemoveTarget(member);
            }}
            style={{ color: "var(--color-error)", whiteSpace: "nowrap" }}
          >
            <UserMinus aria-hidden="true" size={14} />
            Remove
          </button>
        );
      },
    };

    return {
      ...teamMemberListConfig,
      columns: [...teamMemberListConfig.columns, actionColumn],
    };
  }, [account, canRemove]);

  function closeRemoveDialog() {
    if (removeMutation.isPending) return;
    removeMutation.reset();
    setRemoveTarget(null);
  }

  return (
    <>
      <ListPage config={config} />
      <FormDialog
        open={removeTarget !== null}
        onClose={closeRemoveDialog}
        title="Remove team member?"
        description={
          removeTarget ? (
            <>
              Remove <strong>{removeTarget.full_name}</strong> from this organization?
              Their dashboard access and active sessions end immediately. Donation
              history is preserved, and they can be invited again later.
            </>
          ) : undefined
        }
        fields={[]}
        values={{}}
        onChange={() => {}}
        onSubmit={async () => {
          if (!removeTarget) return;
          try {
            await removeMutation.mutateAsync(removeTarget.id);
          } catch {
            // React Query retains the error; FormDialog renders it above.
          }
        }}
        submitLabel="Remove member"
        cancelLabel="Keep member"
        busy={removeMutation.isPending}
        error={removeMutation.isError ? removeErrorMessage(removeMutation.error) : undefined}
        size="sm"
      />
    </>
  );
}
